import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Pressable,
  TouchableOpacity,
  Image,
  Text,
  ScrollView,
  Animated,
  StyleSheet,
  Platform,
  Linking,
  useWindowDimensions,
} from "react-native";
import { useTranslation } from "../../../i18n/useTranslation";
import type { KeyboardTypeOptions, TextInput as RNTextInput } from "react-native";
import Colors from "../../../constants/colors";
import { ComponentStyles } from "../../../constants/componentStyles";
import CloseIcon from "../../../../../../assets/images/CloseIcon.svg";
import NeedHelpIcon from "../../../../../../assets/images/HelpContainer.svg";
import PhoneIcon from "../../../../../../assets/images/CallerIcon.svg";

type Props = {
  closeNeedHelp?: () => void;
  handleNeedHelpCall?: () => void;
};

const NeedHelpComponent: React.FC<Props> = ({
  closeNeedHelp,
  handleNeedHelpCall
}) => {
  const { t } = useTranslation();
  const { height: viewportHeight } = useWindowDimensions();
  const modalMaxHeight = Math.max(320, Math.floor(viewportHeight * 0.9));

  const openDialer = async (phoneNumber: string) => {
    const url = `tel:${phoneNumber}`;
    try {
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        alert(`Call this number manually: ${phoneNumber}`);
      }
    } catch (e) {
      console.error("Failed to open dialer:", e);
    }
  };

  const helpPhone = '(800) 794-2805';
  return (
    <View style={styles.modalOverlay}>
      <Pressable
        style={StyleSheet.absoluteFill}
        onPress={closeNeedHelp}
        accessibilityRole="button"
        accessibilityLabel="Close need help modal"
      />
      <View style={[styles.modalContent, { maxHeight: modalMaxHeight }]}>
        <TouchableOpacity testID="closeButton"
          onPress={closeNeedHelp}
          style={styles.closeButtonContainer}
        >
          <CloseIcon width={24} height={24}/>
        </TouchableOpacity>
        <ScrollView
          style={styles.scrollArea}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator
          persistentScrollbar
          indicatorStyle="black"
          bounces={false}
        >
          <View style={{ alignItems:'center', paddingTop: 8 }}>
            <NeedHelpIcon width={64} height={64}/>
            <Text style={styles.accountLock}>
              {t("NEED_HELP")}
            </Text>
            <Text style={styles.accountLockInfo}>
              {t("NEED_HELP_INFO")}
            </Text>
          </View>
          <TouchableOpacity testID="callNowButton"
            style={styles.continueButton}
            onPress={() => openDialer(helpPhone)}
          >
            <PhoneIcon width={24} height={24}/>
            <Text style={styles.continueButtonText}>
              {t("CALL_NOW")}
            </Text>
            
          </TouchableOpacity>
        </ScrollView>
      </View>
    </View>
  );
};

export default NeedHelpComponent;

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)"
  },
  modalContent: {
    width: "100%",
    flexShrink: 1,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  closeButtonContainer: {
    alignItems: 'flex-end',
    marginRight: 24,
    marginTop: 16,
    marginBottom: 4,
  },
  scrollArea: {
    width: "100%",
    flexShrink: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 8,
  },
  accountLock: {
    fontSize: 24,
    fontWeight: "500",
    lineHeight: 30,
    fontFamily: 'Rubik',
    marginTop: 26,
    marginBottom: 12,
    color: Colors.titleTextColor,
  },
  accountLockInfo: {
    fontSize: 16,
    fontWeight: "400",
    lineHeight: 24,
    fontFamily: 'Rubik',
    marginBottom: 32,
    color: Colors.subTitleColor,
    textAlign: 'center',
    paddingHorizontal: 52
  },
  horizantalLine: {
    borderWidth: 1,
    borderColor: Colors.textInputBackground,
  },
  continueButton: {
    flexDirection: 'row',
    backgroundColor: Colors.secondaryText,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: 'center',
    height: 48,
    marginTop: 16,
    marginBottom: 40,
    marginHorizontal: 24
  },
  continueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.buttonTextColor,
    lineHeight: 20,
    marginLeft: 8
  },
});